import {equals, filter, find, flow, head, pipe, toNumber} from '@logistics-frontend/utils'

import {
  type PaymentContract,
  PaymentContractErrorCode,
  PaymentContractPaymentType,
  PaymentContractType,
} from '../../../model/payment'
import {paymentMethodContractGetter, paymentMethodFinanceGetter, paymentMethodsListGetter} from '../../../repo/cargo/'
import type {PaymentMethodContract, PaymentMethodFinance, PaymentMethodsListResponse} from '../../../repo/cargo/types'
import {
  type ContractPaymentType,
  type ContractType,
  type PaymentMethodActualStateResponse,
  type ValidationErrorCode,
  paymentMethodActualStateGetter,
} from '../../../repo/dcaa'

// Payment Methods List Getters
const getMethods = paymentMethodsListGetter('methods')

// Payment Method Finance Getters
const getDetailsType = paymentMethodFinanceGetter('details.type')
const getIsSelected = paymentMethodFinanceGetter('is_selected')

// Payment Method Contract Getters
const getId = paymentMethodContractGetter('details.contract_id')
const getInnerId = paymentMethodContractGetter('id')

// Payment Method Actual State Getters
const getType = paymentMethodActualStateGetter('payment_method_info.contract_type')
const getPaymentType = paymentMethodActualStateGetter('payment_method_info.contract_payment_type')
const getThreshold = paymentMethodActualStateGetter('financial_info.deactivate_threshold')
const getPenalty = paymentMethodActualStateGetter('financial_info.penalty')
const getActivationPaymentAmount = paymentMethodActualStateGetter('financial_info.offer_activation_payment_amount')
const getDebtsAmount = paymentMethodActualStateGetter('financial_info.debts_amount')
const getBalance = paymentMethodActualStateGetter('financial_info.balance')
const getCurrencySign = paymentMethodActualStateGetter('financial_info.currency_sign')
const getMessages = paymentMethodActualStateGetter('validation_error_codes')
const getFirstMessage = flow(getMessages, head)

// Constants
const contractTypeMap: Readonly<Record<ContractType, PaymentContract['type']>> = {
  paper: PaymentContractType.Paper,
  offer: PaymentContractType.Offer,
  unknown: PaymentContractType.Unknown,
}

const errorCodeMap: Readonly<Record<ValidationErrorCode, PaymentContract['errorCode']>> = {
  outstanding_debt: PaymentContractErrorCode.OutstandingDebt,
  inactive_contract: PaymentContractErrorCode.InactiveContract,
  offer_not_accepted: PaymentContractErrorCode.OfferNotAccepted,
  balance_not_found_for_prepaid: PaymentContractErrorCode.BalanceNotFoundForPrepaid,
  insufficient_balance: PaymentContractErrorCode.InsufficientBalance,
  disabled_contract: PaymentContractErrorCode.DisabledContract,
  not_is_registered: PaymentContractErrorCode.NotIsRegistered,
  card_for_direct_debiting_is_missing: PaymentContractErrorCode.CardForDirectDebitingIsMissing,
  contract_is_pending: PaymentContractErrorCode.ContractIsPending,
  unknown_error: PaymentContractErrorCode.Unknown,
}

const paymentTypeMap: Readonly<Record<ContractPaymentType, PaymentContract['paymentType']>> = {
  postpaid: PaymentContractPaymentType.Postpaid,
  prepaid: PaymentContractPaymentType.Prepaid,
  unknown: PaymentContractPaymentType.Unknown,
}

// Mappers
function toErrorCode(errorCode?: ValidationErrorCode): PaymentContract['errorCode'] {
  return errorCode ? errorCodeMap[errorCode] : undefined
}

function toType(type?: ContractType) {
  return type ? contractTypeMap[type] : PaymentContractType.Unknown
}

function toPaymentType(paymentType?: ContractPaymentType) {
  return paymentType ? paymentTypeMap[paymentType] : PaymentContractPaymentType.Unknown
}

// Predicates
type ContractPaymentGuard = (payload?: PaymentMethodFinance) => payload is PaymentMethodContract
const isContractMethod = flow(getDetailsType, equals('contract')) as ContractPaymentGuard

// @ts-expect-error<TS2322> – не сходятся типы у функций в композиции
const getSelectedContract: (response: PaymentMethodsListResponse) => PaymentMethodContract | undefined = flow(
  getMethods,
  filter(isContractMethod),
  find(getIsSelected),
)

export function toPaymentContract(
  paymentMethodsResponse: PaymentMethodsListResponse,
  actualState: PaymentMethodActualStateResponse,
): PaymentContract {
  const selectedContract = getSelectedContract(paymentMethodsResponse)
  const balance = pipe(actualState, getBalance, toNumber)
  const errorCode = pipe(actualState, getFirstMessage, toErrorCode)
  const threshold = pipe(actualState, getThreshold, toNumber)
  const type = pipe(actualState, getType, toType)
  const paymentType = pipe(actualState, getPaymentType, toPaymentType)
  const penalty = pipe(actualState, getPenalty, toNumber)
  const debtAmount = pipe(actualState, getDebtsAmount, toNumber)
  const activationPaymentAmount = pipe(actualState, getActivationPaymentAmount, toNumber)

  return {
    __type: 'contract',
    id: selectedContract ? getId(selectedContract) : undefined,
    internalID: selectedContract ? getInnerId(selectedContract) : undefined,
    currencySign: getCurrencySign(actualState),
    debtAmount,
    activationPaymentAmount,
    balance,
    errorCode,
    paymentType,
    penalty,
    threshold,
    type,
    isBalanceBelowThreshold() {
      return errorCode === PaymentContractErrorCode.InsufficientBalance
    },
    isPending() {
      return (
        errorCode === PaymentContractErrorCode.ContractIsPending ||
        errorCode === PaymentContractErrorCode.BalanceNotFoundForPrepaid
      )
    },
  }
}
